import { useAuth } from "@/context/AuthContext";
import { settings } from "@/settings";
import { SWRConfiguration } from "swr";

export class SWRApiError extends Error {
  statusCode: number;
  statusText: string;
  detail?: unknown;

  constructor({
    statusCode,
    statusText,
    message,
    detail,
  }: {
    message: string;
    statusCode: number;
    statusText: string;
    detail?: unknown;
  }) {
    super(message);

    this.name = "SWRApiError";
    this.statusCode = statusCode;
    this.statusText = statusText;
    this.detail = detail;
  }
}

export const swrConfigOptions: SWRConfiguration = {
  keepPreviousData: true,
  revalidateOnFocus: true,
  errorRetryCount: 1,
};

const createSWRApiError = (response?: { status: number; statusText: string }) =>
  new SWRApiError({
    message: response?.statusText ?? "Network Error",
    statusCode: response?.status ?? 500,
    statusText: response?.statusText ?? "Network Error",
  });

const _handleApiResponse = async (response: Response) => {
  if (response.ok) {
    return response.json();
  }

  if (response.status === 404) return;

  if (response.status === 401) {
    return Promise.reject(
      createSWRApiError({
        status: response.status,
        statusText: "Unauthenticated. Your access token might be expired.",
      }),
    );
  }

  // Handle error from backend
  if (typeof response.json === "function") {
    const apiError = await response.json();

    const rawDetail = typeof apiError === "string" ? undefined : apiError.detail;
    const parsedApiError =
      typeof apiError === "string"
        ? apiError
        : (apiError.detail?.message ?? apiError.message ?? apiError.detail);

    return Promise.reject(
      new SWRApiError({
        statusCode: response.status,
        statusText: parsedApiError,
        message: parsedApiError,
        detail: rawDetail,
      }),
    );
  }

  // Throw a fallback error in case no error from the backend can be determined
  return Promise.reject(createSWRApiError());
};

type FetcherParams = {
  url: string;
  accessToken?: string;
};

export const useFetcher = () => {
  const { accessToken } = useAuth();

  return async function fetcher<Response>({
    url,
    accessToken: _overrideAccessToken,
  }: FetcherParams): Promise<Response> {
    try {
      let headers = {};

      if (_overrideAccessToken) {
        headers = {
          Authorization: `Bearer ${_overrideAccessToken}`,
        };
      } else {
        headers = {
          Authorization: `Bearer ${accessToken}`,
        };
      }

      const fetchResponse = await fetch(`${settings.apiBaseUrl}${url}`, {
        headers,
      });

      return _handleApiResponse(fetchResponse);
    } catch {
      const fallbackError = createSWRApiError();
      return Promise.reject(fallbackError);
    }
  };
};

export const useMutater = () => {
  const { accessToken } = useAuth();

  return async function mutater<Data>(url: string, { arg }: { arg: Data }): Promise<void> {
    try {
      const fetchResponse = await fetch(`${settings.apiBaseUrl}${url}`, {
        method: "POST",
        body: JSON.stringify({ ...arg }),
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      });

      return _handleApiResponse(fetchResponse);
    } catch {
      return Promise.reject(createSWRApiError());
    }
  };
};

type Input = Record<string, any>;
export const useUploader = () => {
  const { accessToken } = useAuth();

  return async function uploader<Data>(url: string, { arg }: { arg: Data }): Promise<any> {
    try {
      const formData = new FormData();

      const _arg = arg as Input;
      const keys = Object.keys(_arg ?? {});

      keys.forEach((key) => {
        formData.append(key, _arg[key]);
      });

      const fetchResponse = await fetch(`${settings.apiBaseUrl}${url}`, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      return _handleApiResponse(fetchResponse);
    } catch {
      return Promise.reject(createSWRApiError());
    }
  };
};

export const useUploaderWithResponse = () => {
  const { accessToken } = useAuth();

  return async function uploaderWithResponse<Data, Response>(
    url: string,
    { arg }: { arg: Data },
  ): Promise<Response> {
    try {
      const formData = new FormData();

      const _arg = arg as Input;
      const keys = Object.keys(_arg ?? {});

      keys.forEach((key) => {
        formData.append(key, _arg[key]);
      });

      const fetchResponse = await fetch(`${settings.apiBaseUrl}${url}`, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      return _handleApiResponse(fetchResponse);
    } catch {
      return Promise.reject(createSWRApiError());
    }
  };
};

export type UpdaterParams = { url: string; action?: string };
export const useUpdater = () => {
  const { accessToken } = useAuth();

  return async function updater<Data>(
    params: UpdaterParams,
    { arg }: { arg: Data & { id: string | number } },
  ): Promise<void> {
    try {
      const { url, action } = params;
      const { id, ...body } = arg;
      const fetchResponse = await fetch(
        `${settings.apiBaseUrl}${url}/${id}${action ? `/${action}` : ""}`,
        {
          method: "PATCH",
          body: JSON.stringify({ ...body }),
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        },
      );

      return _handleApiResponse(fetchResponse);
    } catch {
      return Promise.reject(createSWRApiError());
    }
  };
};

export const useDeleter = () => {
  const { accessToken } = useAuth();

  return async function deleter(
    url: string,
    { arg }: { arg: { id: string | number; [key: string]: unknown } },
  ): Promise<void> {
    try {
      const { id, ...body } = arg;

      const fetchResponse = await fetch(`${settings.apiBaseUrl}${url}/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined,
      });

      return _handleApiResponse(fetchResponse);
    } catch {
      return Promise.reject(createSWRApiError());
    }
  };
};

export const useDeleteUrl = () => {
  const { accessToken } = useAuth();

  return async function deleteUrl(url: string): Promise<void> {
    try {
      const fetchResponse = await fetch(`${settings.apiBaseUrl}${url}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      return _handleApiResponse(fetchResponse);
    } catch {
      return Promise.reject(createSWRApiError());
    }
  };
};
